/**
 * useZtnaJobRealtime — consome os eventos SSE de onboarding ZTNA no feed unificado.
 *
 * Montado no Dashboard (sempre vivo em /admin/ztna). Reusa a conexão SSE
 * compartilhada via useNotificationsRealtime.onEvent — sem novo transporte.
 *
 *  - `ztna.job.progress` → acumula linha no ztnaJobLogStore + invalida o cache do job
 *  - `ztna.job.finished` → invalida apps/kpis/history/jobs + expõe `finished`
 *    (dirige o card de resultado in-place e o highlight do card novo no grid)
 *
 * Aditivo: se o SSE cair, o polling do JobBanner/Drawer continua funcionando.
 */
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useNotificationsRealtime,
  type RealtimeEvent,
} from '@/hooks/useNotificationsRealtime'
import { ztnaJobLogStore } from './ztnaJobLogStore'

export interface JobFinished {
  jobId: number
  status: string
  slug: string
  hostname: string
  error: string | null
}

export function useZtnaJobRealtime(): {
  finished: JobFinished | null
  clearFinished: () => void
} {
  const qc = useQueryClient()
  const [finished, setFinished] = useState<JobFinished | null>(null)

  const onEvent = useCallback(
    (raw: RealtimeEvent) => {
      const ev = raw as RealtimeEvent & Record<string, unknown>
      if (typeof ev.type !== 'string' || !ev.type.startsWith('ztna.job.')) return

      if (ev.type === 'ztna.job.progress' && typeof ev.job_id === 'number') {
        ztnaJobLogStore.append(ev.job_id, {
          step: String(ev.step ?? ''),
          phase: String(ev.phase ?? 'done'),
          percent: Number(ev.percent ?? 0),
          message: String(ev.message ?? ''),
          elapsed_seconds: Number(ev.elapsed_seconds ?? 0),
          ts: Date.now(),
        })
        qc.invalidateQueries({ queryKey: ['ztna', 'jobs', ev.job_id] })
      } else if (ev.type === 'ztna.job.finished' && typeof ev.job_id === 'number') {
        qc.invalidateQueries({ queryKey: ['ztna', 'apps'] })
        qc.invalidateQueries({ queryKey: ['ztna', 'kpis'] })
        qc.invalidateQueries({ queryKey: ['ztna', 'onboarding-history'] })
        qc.invalidateQueries({ queryKey: ['ztna', 'jobs'] })
        setFinished({
          jobId: ev.job_id,
          status: String(ev.status ?? ''),
          slug: String(ev.slug ?? ''),
          hostname: String(ev.hostname ?? ''),
          error: (ev.error as string | null | undefined) ?? null,
        })
      }
    },
    [qc],
  )

  useNotificationsRealtime({}, { onEvent })

  const clearFinished = useCallback(() => setFinished(null), [])
  return { finished, clearFinished }
}
